import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  UseControllerOptions,
  useForm,
  useFormContext,
} from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import { colors, media, Option, Theme, utils } from '../../../core';
import Button from '../Button';
import { DatePickerProps } from '../DatePicker';
import Label from '../Label';
import LinkButton from '../LinkButton';
import Modal from '../Modal';
import { RadioGroupProps, RadioInputProps } from '../RadioGroup';
import { SelectInputProps } from '../SelectInput/SelectInput.types';
import Tag from '../Tag';
import { TagInputProps } from '../TagGroup';
import Text from '../Text';
import { TextInputProps } from '../TextInput';
import { TextInputMaskProps } from '../TextInputMask';

export interface FormProps {
  values: { [key: string]: string | { label: string; value: string } } | any;
  onSubmit: any;
  onRemove?: () => void;
  onCancel?: () => void;
  onClear?: () => void;
  submitButtonText?: string;
  removeButtonText?: string;
  edit: boolean;
  shouldReset?: boolean;
  direction?: 'column' | 'row';
  variant?: 'primary' | 'secondary';
  children: React.ReactNode;
  isSubmitOnEnter?: boolean;
  showDisabledSubmitButton?: boolean;
  buttonWidth?: string;
  disableButtonSubmit?: boolean;
  buttonsPosition?: 'left' | 'center' | 'right';
  noButtons?: boolean;
  buttonSize?: 'xsmall' | 'small' | 'medium' | 'large';
}

export interface FormContextProps {
  edit: boolean;
  values?: { [key: string]: string | { label: string; value: string } };
}

interface StyledFormProps {
  direction: 'row' | 'column';
  onSubmit: (data: any) => void;
}

interface ControllerProps {
  as?: any; // TODO (the same as react-hook-form Controller props)
  render?: any; // TODO (the same as react-hook-form Controller props)
  errorMessage?: string;
  errorMessageOnDisable?: string;
  defaultValue?: Option | string;
  isCheckbox?: boolean;
  isTagGroup?: boolean;
  isRadio?: boolean;
  isSlotCalendar?: boolean;
  isTag?: boolean;
  shouldReset?: boolean;
}

type FormRadioInputProps = ControllerProps &
  UseControllerOptions &
  RadioGroupProps &
  RadioInputProps;
type FormSelectInputProps = ControllerProps & UseControllerOptions & SelectInputProps;
type FormTextInputProps = ControllerProps &
  UseControllerOptions &
  TextInputProps &
  TextInputMaskProps;
type DatePickerInputProps = ControllerProps & UseControllerOptions & DatePickerProps;
type FormInputProps =
  | DatePickerInputProps
  | FormTextInputProps
  | FormRadioInputProps
  | FormSelectInputProps;

const StyledForm = styled.form<StyledFormProps>`
  display: flex;
  flex-direction: ${({ direction }) => direction};
  z-index: 0;
  position: relative;

  ${({ direction }) =>
    direction === 'row' &&
    `
      align-items: flex-end;
      justify-content: space-between;
    `}
`;

const SubmitButtonsWrapper = styled.div<{
  direction: 'row' | 'column';
  buttonsPosition: 'left' | 'center' | 'right';
}>`
  padding: ${({ direction, theme }) =>
    direction === 'row' ? '0 0 0 20px' : theme.name === Theme.theme3 ? '0' : '32px 0 0'};
  display: flex;
  align-items: center;
  justify-content: ${({ buttonsPosition }) => {
    switch (buttonsPosition) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      default:
        return 'center';
    }
  }};

  ${media.tabletPortrait} {
    justify-content: center;
    padding-right: 16px;
    padding-left: 16px;
  }
`;

const ClearButton = styled(LinkButton).attrs({
  type: 'reset',
})`
  background-color: transparent;
  border: 0;
  margin: 0 40px 0 20px;
`;

const RowLabel = styled(Text).attrs({
  as: 'label',
})<{ padding?: string }>`
  display: block;
  text-align: left;
  font-weight: ${({ theme }) => theme.formRowLabelFontWeight || 600};
  font-size: ${({ theme }) => theme.formRowLabelFontSize || '16px'};
  letter-spacing: 0;
  color: ${({ theme }) => theme.formRowLabelColor || colors.charcoal};
  padding: ${({ theme, padding }) => padding || theme.formRowLabelPadding || 0};
  margin: 0;
`;

const Row = styled.div<{
  noMargin?: boolean;
  margin?: string;
  column?: number | string;
  showAlwaysColumnValues?: boolean;
  rowGap?: string;
}>`
  display: grid;
  grid-template-columns: ${({ column }) =>
    typeof column === 'string'
      ? column
      : column
      ? `repeat(${column}, ${(100 - 2 * (column - 1)) / column}%)`
      : 'repeat(3, 32%)'};
  column-gap: 2%;
  row-gap: ${({ theme, rowGap }) => rowGap || theme.formRowGap || '20px'};
  margin: ${({ noMargin, margin }) => (noMargin ? 0 : margin ? margin : '0 0 48px')};
  position: relative;

  ${media.tabletPortrait} {
    grid-template-columns: ${({ showAlwaysColumnValues, column }) =>
      showAlwaysColumnValues ? column : '1fr'};
    margin: ${({ noMargin, margin }) => (noMargin ? 0 : margin ? margin : '0 0 20px')};
  }
`;

const StyledLabel = styled(Label)`
  word-break: break-all;
`;

const StyledText = styled(Text)`
  word-break: break-all;
`;

const StyledButton = styled(Button)`
  margin: 0 20px;
`;

export const FormContext = createContext<FormContextProps>({
  edit: true,
});

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
`;

const Input = (props: FormInputProps) => {
  const { control, errors } = useFormContext();
  const { edit, values }: FormContextProps = useContext(FormContext);

  const value = props.defaultValue ? props.defaultValue : values?.[`${props.name}`];
  const errorMessage = props.errorMessage ? props.errorMessage : errors?.[`${props.name}`]?.message;

  const renderFieldText = () => {
    if (value === 'true' || value === 'false') {
      return utils.capitalize(value);
    }

    return value ? (typeof value !== 'string' ? (value.label ? value.label : '-') : value) : '-';
  };

  // editable input
  if (edit) {
    return (
      <Controller
        control={control}
        errorMessage={errorMessage}
        defaultValue={value}
        showRequired={props.rules?.required}
        {...props}
      />
    );
  }

  // hide checkbox input
  if (props.isCheckbox) {
    return null;
  }

  // TagGroup
  if (props.isTagGroup) {
    return (
      <div>
        <StyledLabel>{props.label}</StyledLabel>

        {Array.isArray(value)
          ? value.map((item: TagInputProps) => (
              <Tag color={colors.ash} key={item.name}>
                {item.label}
              </Tag>
            ))
          : null}
      </div>
    );
  }

  // Tag
  if (props.isTag) {
    return (
      <div>
        <StyledLabel>{props.label}</StyledLabel>
        <Tag color={colors.ash}>{renderFieldText()}</Tag>
      </div>
    );
  }

  // TextInput, SelectInput
  return (
    <div>
      <StyledLabel>{props.label}</StyledLabel>
      <StyledText>{renderFieldText()}</StyledText>
      {props.errorMessageOnDisable ? (
        <ErrorMessage>{props.errorMessageOnDisable}</ErrorMessage>
      ) : null}
    </div>
  );
};

const Form = ({
  values,
  onSubmit,
  onRemove,
  onCancel,
  onClear,
  edit,
  shouldReset,
  submitButtonText,
  removeButtonText,
  isSubmitOnEnter = false,
  direction = 'column',
  variant = 'primary',
  children,
  showDisabledSubmitButton,
  buttonWidth,
  disableButtonSubmit = false,
  buttonsPosition,
  noButtons = false,
  buttonSize,
}: FormProps) => {
  const theme = useTheme();
  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: values,
  });

  const { isDirty, isValid } = methods.formState;
  const [isShowingModal, setShowingModal] = useState(false);

  const sanitizeValue = (data: any) => {
    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (typeof value === 'string' && value.length === 0) {
        data[key] = undefined;
      } else if (typeof value === 'object' && value !== null) {
        sanitizeValue(data[key]);
      }
    });
  };

  const onSubmitForm = (data: any) => {
    sanitizeValue(data);
    onSubmit(data);
    setShowingModal(false);
  };

  const onConfirmCancel = () => {
    setShowingModal(true);
  };

  const onCancelForm = () => {
    methods.reset();
    setShowingModal(false);
    onCancel && onCancel();
  };

  const onClearForm = () => {
    methods.reset();
  };

  const onRemoveForm = () => {
    methods.reset();
    setShowingModal(false);
    onRemove && onRemove();
  };

  const checkKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (e.key === 'Enter' && !['TEXTAREA'].includes(target.tagName)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (shouldReset) {
      methods.reset();
    }
  }, [shouldReset, methods]);

  return (
    <FormContext.Provider value={{ edit, values }}>
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={methods.handleSubmit(onSubmitForm)}
          direction={direction}
          onKeyDown={(e: any) => !isSubmitOnEnter && checkKeyDown(e)}
        >
          <div style={{ flex: 1 }}>
            {children}

            {isShowingModal && (
              <Modal
                width="500px"
                title="Confirmation"
                showButtons
                onPress={onCancelForm}
                onClose={() => setShowingModal(false)}
                variant={theme.name === Theme.theme3 ? 'secondary' : 'primary'}
              >
                Are you sure you want to close this window?
              </Modal>
            )}
          </div>

          {edit && !noButtons && (
            <React.Fragment>
              <SubmitButtonsWrapper
                direction={direction}
                buttonsPosition={
                  buttonsPosition
                    ? buttonsPosition
                    : theme.name === Theme.theme3
                    ? 'right'
                    : 'center'
                }
              >
                {onRemove &&
                  (variant === 'secondary' ? (
                    <StyledButton
                      variant="secondary"
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                      onClick={onRemoveForm}
                    >
                      {removeButtonText || 'Remove'}
                    </StyledButton>
                  ) : (
                    <ClearButton
                      onClick={onRemoveForm}
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                    >
                      {removeButtonText || 'Remove'}
                    </ClearButton>
                  ))}

                {onCancel &&
                  (variant === 'secondary' ? (
                    <StyledButton
                      variant="secondary"
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                      onClick={onCancelForm}
                      width={buttonWidth}
                    >
                      Cancel
                    </StyledButton>
                  ) : (
                    <ClearButton
                      onClick={onConfirmCancel}
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                    >
                      Cancel
                    </ClearButton>
                  ))}

                {onClear &&
                  (variant === 'secondary' ? (
                    <StyledButton
                      variant="secondary"
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                      onClick={onClearForm}
                      width={buttonWidth}
                    >
                      Cancel
                    </StyledButton>
                  ) : (
                    <ClearButton
                      onClick={onClearForm}
                      size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                    >
                      Clear
                    </ClearButton>
                  ))}

                {variant === 'secondary' ? (
                  <StyledButton
                    type="submit"
                    size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                    variant="primary"
                    disabled={showDisabledSubmitButton ? !isDirty || !isValid : false}
                    width={buttonWidth}
                  >
                    {submitButtonText ? submitButtonText : 'Save'}
                  </StyledButton>
                ) : (
                  <Button
                    type="submit"
                    size={buttonSize || (direction === 'column' ? 'medium' : 'small')}
                    variant="primary"
                    disabled={
                      disableButtonSubmit
                        ? disableButtonSubmit
                        : showDisabledSubmitButton
                        ? !isDirty || !isValid
                        : false
                    }
                    width={buttonWidth}
                  >
                    {submitButtonText ? submitButtonText : 'Save'}
                  </Button>
                )}
              </SubmitButtonsWrapper>
            </React.Fragment>
          )}
        </StyledForm>
      </FormProvider>
    </FormContext.Provider>
  );
};

Form.Controller = Input;
Form.RowLabel = RowLabel;
Form.Row = Row;

export default Form;
